import { X, ExternalLink, TrendingUp, Lock, Shield, Coins, Brain } from "lucide-react";
import UpgradeModal from "./UpgradeModal";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useCompare } from "../context/CompareContext";
import { useAuthStore } from "../store/authStore";

const API_URL = import.meta.env.VITE_API_URL || 'http://64.23.240.10/api/v1';
const BASE_API_URL = API_URL.replace('/v1', '');

function tierBadgeColorCM(tier: string): string {
  switch (tier) {
    case 'Tier 1': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'Tier 2': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'Tier 3': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'Tier 4': return 'bg-red-500/20 text-red-400 border-red-500/30';
    default: return 'bg-sky-blue/20 text-sky-blue border-sky-blue/30';
  }
}

function gradeBadgeColorCM(grade: string): string {
  if (!grade) return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  const g = grade.charAt(0);
  switch (g) {
    case 'A': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'B': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'C': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'D': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
}

interface CompareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CompareModal({ isOpen, onClose }: CompareModalProps) {
  const { compareList, removeFromCompare, clearCompare, limitReached, dismissLimitReached } =
    useCompare();
  const { isAuthenticated, user } = useAuthStore();
  const tier = user?.subscriptionTier || "free";
  const isPro = tier === "pro" || tier === "premium";
  const isStarterPlus = tier === "starter" || isPro;

  const [intelData, setIntelData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!isOpen || compareList.length === 0) return;
    const { token: authToken } = useAuthStore.getState();
    const hdrs: Record<string, string> = {};
    if (authToken) hdrs['Authorization'] = `Bearer ${authToken}`;
    compareList.forEach(opt => {
      const slug = (opt as any).slug;
      if (!slug || intelData[slug]) return;
      fetch(`${BASE_API_URL}/intelligence/protocol/${slug}`, { headers: hdrs })
        .then(r => r.json())
        .then(d => {
          if (d && !d.error) setIntelData(prev => ({ ...prev, [slug]: d.data || d }));
        })
        .catch(() => {});
    });
  }, [isOpen, compareList]);

  const formatAPY = (apy: number | string) => {
    const val = typeof apy === "string" ? parseFloat(apy) : apy;
    if (!val || isNaN(val)) return "N/A";
    return `${val.toFixed(2)}%`;
  };

  const formatTVL = (tvl: number | string) => {
    const val = typeof tvl === "string" ? parseFloat(tvl) : tvl;
    if (!val || isNaN(val)) return "N/A";
    if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
    if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
    if (val >= 1e3) return `$${(val / 1e3).toFixed(2)}K`;
    return `$${val.toFixed(2)}`;
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "low":
        return "text-green-400 bg-green-400/10";
      case "medium":
        return "text-yellow-400 bg-yellow-400/10";
      case "high":
        return "text-red-400 bg-red-400/10";
      default:
        return "text-sky-blue bg-sky-blue/10";
    }
  };

  const showFullData = isAuthenticated;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-navy-dark border border-sky-blue/20 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
          >
            <div className="bg-gradient-cta p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Compare Protocols</h2>
                  <p className="text-white/80 text-sm mt-1">
                    {compareList.length}{" "}
                    {compareList.length === 1 ? "protocol" : "protocols"} selected
                    {!isAuthenticated && (
                      <span className="ml-2">(max 2 — sign up for more)</span>
                    )}
                    {isAuthenticated && tier === "free" && (
                      <span className="ml-2">(max 2 — upgrade for more)</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={clearCompare}
                    className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white/20 rounded-lg transition"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
              {/* Limit-reached upgrade prompts */}
              {limitReached && !isAuthenticated && (
                <div className="mb-6 p-5 bg-gradient-to-r from-neon-red/10 to-electric-blue/10 border border-neon-red/30 rounded-xl relative">
                  <button
                    onClick={dismissLimitReached}
                    className="absolute top-3 right-3 text-sky-blue/50 hover:text-cream transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <h3 className="text-lg font-bold text-cream mb-2">
                    Create a free account to compare up to 2 protocols — no credit card required.
                  </h3>
                  <div className="flex gap-3 mt-3">
                    <Link
                      to="/register"
                      onClick={onClose}
                      className="btn-neon inline-flex items-center gap-2 px-5 py-2"
                    >
                      Sign Up Free
                    </Link>
                  </div>
                </div>
              )}

              <UpgradeModal
                isOpen={limitReached && isAuthenticated && tier === "free"}
                onClose={dismissLimitReached}
                feature="comparison"
                currentLimit={2}
              />

              {compareList.length === 0 ? (
                <div className="text-center py-12">
                  <TrendingUp className="w-16 h-16 mx-auto text-sky-blue/30 mb-4" />
                  <h3 className="text-xl font-semibold text-cream mb-2">
                    No protocols selected
                  </h3>
                  <p className="text-sky-blue">
                    Click the compare button on staking cards to add them here
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-sky-blue/20">
                        <th className="text-left py-4 px-4 font-semibold text-sky-blue sticky left-0 bg-navy-dark">
                          Feature
                        </th>
                        {compareList.map((option) => {
                          const opt = option as any;
                          return (
                            <th key={option.id} className="py-4 px-6 min-w-[250px]">
                              <div className="flex flex-col items-center">
                                <img
                                  src={opt.logo || opt.logoUrl || "/fallback-logo.png"}
                                  alt={option.name}
                                  className="w-12 h-12 rounded-full mb-2"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = "none";
                                  }}
                                />
                                <div className="font-semibold text-cream text-center">
                                  {option.name}
                                </div>
                                <div className="text-sm text-sky-blue">{option.symbol}</div>
                                <button
                                  onClick={() => removeFromCompare(option.id)}
                                  className="mt-2 text-xs text-neon-red hover:underline"
                                >
                                  Remove
                                </button>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-neon-red" /> APY
                          </div>
                        </td>
                        {compareList.map((option) => (
                          <td key={option.id} className="py-4 px-6 text-center">
                            <div className="text-2xl font-bold gradient-text-neon">
                              {formatAPY(option.apy)}
                            </div>
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          Category
                        </td>
                        {compareList.map((option) => (
                          <td key={option.id} className="py-4 px-6 text-center">
                            <span className="inline-block px-2 py-1 bg-steel-blue/20 rounded text-sm text-cream">
                              {option.category}
                            </span>
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          <div className="flex items-center gap-2">
                            <Coins className="w-4 h-4 text-neon-red" /> Total Value Locked
                          </div>
                        </td>
                        {compareList.map((option) => (
                          <td key={option.id} className="py-4 px-6 text-center">
                            {showFullData ? (
                              <div className="text-lg font-semibold text-cream">
                                {formatTVL(option.tvl)}
                              </div>
                            ) : (
                              <div className="text-lg font-semibold text-steel-blue/40 blur-sm select-none">
                                $1.23B
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-neon-red" /> Risk Level
                          </div>
                        </td>
                        {compareList.map((option) => {
                          const risk =
                            (option as any).risk_level ||
                            (option as any).riskLevel ||
                            (option as any).risk ||
                            "medium";
                          return (
                            <td key={option.id} className="py-4 px-6 text-center">
                              {showFullData ? (
                                <span
                                  className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(risk)}`}
                                >
                                  {risk}
                                </span>
                              ) : (
                                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium text-steel-blue/40 bg-steel-blue/10 blur-sm select-none">
                                  medium
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          Network
                        </td>
                        {compareList.map((option) => (
                          <td key={option.id} className="py-4 px-6 text-center">
                            {showFullData ? (
                              <div className="text-cream">
                                {(option as any).network || "-"}
                              </div>
                            ) : (
                              <div className="text-steel-blue/40 blur-sm select-none">
                                Ethereum
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          Minimum Stake
                        </td>
                        {compareList.map((option) => (
                          <td key={option.id} className="py-4 px-6 text-center">
                            {showFullData ? (
                              <div className="text-cream">
                                {option.minStake
                                  ? `${option.minStake} ${option.symbol}`
                                  : "No minimum"}
                              </div>
                            ) : (
                              <div className="text-steel-blue/40 blur-sm select-none">
                                32 ETH
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-neon-red" /> Lock Period
                          </div>
                        </td>
                        {compareList.map((option) => (
                          <td key={option.id} className="py-4 px-6 text-center">
                            {showFullData ? (
                              <div className="text-cream">
                                {option.lockPeriod || "Flexible"}
                              </div>
                            ) : (
                              <div className="text-steel-blue/40 blur-sm select-none">
                                Flexible
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>

                      {/* Intelligence Rows */}
                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          <div className="flex items-center gap-2">
                            <Brain className="w-4 h-4 text-neon-red" /> TVL Tier
                          </div>
                        </td>
                        {compareList.map((option) => {
                          const slug = (option as any).slug;
                          const intel = slug ? intelData[slug] : null;
                          const tvlTier = intel?.tvl_tier || intel?.tvlTier || '';
                          const tvlLabel = intel?.tvl_tier_label || intel?.tvlTierLabel || '';
                          return (
                            <td key={option.id} className="py-4 px-6 text-center">
                              {isAuthenticated ? (
                                tvlTier ? (
                                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${tierBadgeColorCM(tvlTier)}`}>
                                    {tvlTier}{tvlLabel ? ` \u2014 ${tvlLabel}` : ''}
                                  </span>
                                ) : <span className="text-sky-blue/40">-</span>
                              ) : (
                                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium text-steel-blue/40 bg-steel-blue/10 blur-sm select-none">Tier 1</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          Overall Grade
                        </td>
                        {compareList.map((option) => {
                          const slug = (option as any).slug;
                          const intel = slug ? intelData[slug] : null;
                          const grade = intel?.grade || '';
                          return (
                            <td key={option.id} className="py-4 px-6 text-center">
                              {isAuthenticated ? (
                                grade ? (
                                  <span className={`inline-block px-3 py-1.5 rounded-lg text-lg font-bold border ${gradeBadgeColorCM(grade)}`}>{grade}</span>
                                ) : <span className="text-sky-blue/40">-</span>
                              ) : (
                                <span className="inline-block px-3 py-1.5 rounded-lg text-lg font-bold text-steel-blue/40 bg-steel-blue/10 blur-sm select-none">B+</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          APY Stability
                        </td>
                        {compareList.map((option) => {
                          const slug = (option as any).slug;
                          const intel = slug ? intelData[slug] : null;
                          const score = intel?.stability_score ?? intel?.stabilityScore;
                          return (
                            <td key={option.id} className="py-4 px-6 text-center">
                              {isStarterPlus && score != null ? (
                                <div className="flex items-center justify-center gap-2">
                                  <div className="w-16 h-2 bg-navy-dark/50 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${score >= 70 ? 'bg-green-400' : score >= 40 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{ width: `${Math.min(100, score)}%` }} />
                                  </div>
                                  <span className="text-cream font-medium text-sm">{score}</span>
                                </div>
                              ) : (
                                <span className="text-steel-blue/40 blur-sm select-none">72/100</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          Risk Score
                        </td>
                        {compareList.map((option) => {
                          const slug = (option as any).slug;
                          const intel = slug ? intelData[slug] : null;
                          const riskVal = intel?.risk_score ?? intel?.riskScore;
                          return (
                            <td key={option.id} className="py-4 px-6 text-center">
                              {isPro && riskVal != null ? (
                                <span className={`font-bold text-lg ${riskVal >= 75 ? 'text-green-400' : riskVal >= 50 ? 'text-yellow-400' : riskVal >= 25 ? 'text-orange-400' : 'text-red-400'}`}>{riskVal}</span>
                              ) : (
                                <span className="text-steel-blue/40 blur-sm select-none font-bold">65</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr className="border-b border-sky-blue/10">
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          Adjusted Yield
                        </td>
                        {compareList.map((option) => {
                          const slug = (option as any).slug;
                          const intel = slug ? intelData[slug] : null;
                          const adjYield = intel?.volatility_adjusted_yield ?? intel?.adjustedYield;
                          return (
                            <td key={option.id} className="py-4 px-6 text-center">
                              {isPro && adjYield != null ? (
                                <span className="font-bold gradient-text-neon">{adjYield}%</span>
                              ) : (
                                <span className="text-steel-blue/40 blur-sm select-none font-bold">2.1%</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td className="py-4 px-4 font-medium text-sky-blue sticky left-0 bg-navy-dark">
                          Action
                        </td>
                        {compareList.map((option) => {
                          const opt = option as any;
                          const stakeUrl =
                            opt.stakeUrl || opt.metadata?.stakeUrl || "#";
                          return (
                            <td key={option.id} className="py-4 px-6 text-center">
                              <a
                                href={stakeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-cta text-white rounded-lg font-medium hover:shadow-neon transition"
                              >
                                Stake Now <ExternalLink className="w-4 h-4" />
                              </a>
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>

                  {/* Contextual yield-difference upgrade nudge */}
                  {compareList.length >= 2 && (() => {
                    const apys = compareList.map(o => typeof o.apy === 'string' ? parseFloat(o.apy) : o.apy).filter(v => v && !isNaN(v));
                    const spread = apys.length >= 2 ? Math.max(...apys) - Math.min(...apys) : 0;
                    if (spread <= 1) return null;
                    const best = compareList.reduce((a, b) => {
                      const aVal = typeof a.apy === 'string' ? parseFloat(a.apy) : a.apy;
                      const bVal = typeof b.apy === 'string' ? parseFloat(b.apy) : b.apy;
                      return (aVal || 0) > (bVal || 0) ? a : b;
                    });
                    if (isPro) return null;
                    return (
                      <div className="mt-4 p-4 bg-gradient-to-r from-sky-blue/5 to-sky-blue/10 border border-sky-blue/20 rounded-xl flex items-center gap-4">
                        <TrendingUp className="w-8 h-8 text-sky-blue flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-cream text-sm font-medium">
                            {best.name} yields {spread.toFixed(2)}% more than the lowest option.
                          </p>
                          <p className="text-sky-blue/60 text-xs mt-0.5">
                            Upgrade to see risk-adjusted yields, stability scores, and whether that extra APY is worth the risk.
                          </p>
                        </div>
                        <Link to="/pricing?source=yield_diff" onClick={onClose} className="px-4 py-2 bg-sky-blue hover:bg-sky-blue/90 text-navy-dark font-bold rounded-lg transition text-sm whitespace-nowrap">
                          See Plans
                        </Link>
                      </div>
                    );
                  })()}

                  {/* Sign-up CTA for non-authenticated (when limit not actively reached) */}
                  {!isAuthenticated && !limitReached && (
                    <div className="mt-6 p-6 bg-gradient-to-r from-neon-red/10 to-electric-blue/10 border border-neon-red/30 rounded-xl text-center">
                      <Lock className="w-8 h-8 text-neon-red mx-auto mb-3" />
                      <h3 className="text-lg font-bold text-cream mb-2">
                        Sign up to unlock full comparison data
                      </h3>
                      <p className="text-sky-blue text-sm mb-4">
                        TVL, risk levels, lock periods, minimum stakes, and compare up to 3
                        protocols — all free.
                      </p>
                      <div className="flex gap-3 justify-center">
                        <Link
                          to="/register"
                          onClick={onClose}
                          className="btn-neon inline-flex items-center gap-2"
                        >
                          Create free account
                        </Link>
                        <Link
                          to="/pricing"
                          onClick={onClose}
                          className="bg-steel-blue/20 border border-sky-blue/30 text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-steel-blue/30 transition-all"
                        >
                          View plans
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Upgrade CTA for free users (when limit not actively reached) */}
                  {isAuthenticated && tier === "free" && !limitReached && (
                    <div className="mt-6 p-4 bg-gradient-to-r from-neon-red/10 to-electric-blue/10 border border-neon-red/30 rounded-xl text-center">
                      <p className="text-sky-blue text-sm mb-3">
                        Free plan: compare up to 2 protocols.{" "}
                        <span className="text-cream font-semibold">
                          Upgrade to Starter for up to 5 — $4.99/mo
                        </span>
                      </p>
                      <Link
                        to="/pricing#starter"
                        onClick={onClose}
                        className="btn-neon inline-block text-sm px-4 py-2"
                      >
                        Upgrade to Starter
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
